import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  TextField,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import DatePickerMonthEnds from "../../common/DatePicker/DatePickerMonthEnds";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import { useUserContext } from "../../../context/UserContext";
import { minDate, maxDate, getIsoStringToDate } from "../../../utils/utils";
import { useApprovalContext } from "../../../context/ApprovalContext";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import { LOCAL_CURRENCY } from "../../../utils/constants";

const mapCurrency = (currency) => ({
  id: currency,
  name: currency,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const getApprovalDotColor = (status) => {
  switch ((status || "").toUpperCase()) {
    case "APPROVED":
      return "#2e7d32";
    case "REJECTED":
      return "#d32f2f";
    case "PENDING":
      return "#f9a825";
    case "NOT STARTED":
      return "#9e9e9e";
    default:
      return "#9e9e9e";
  }
};

const ApprovalDot = ({ status }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: getApprovalDotColor(status),
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

const groupKeyForMetric = (metric) =>
  metric?.dimension2
    ? `${metric.dimension}_${metric.dimension2}`
    : metric?.dimension;

const labelForGroup = (groupKey) => groupKey?.replace("_", " -> ");

const APPROVAL_KEY_SEPARATOR = "|";

const KRISummarySubmissionGlobalFiltersAndReports = () => {
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const [isTouched, setIsTouched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [approvalError, setApprovalError] = useState("");
  const [pendingApprovalType, setPendingApprovalType] = useState(null);
  const [pendingApprovalRegion, setPendingApprovalRegion] = useState(null);

  const {
    selectedMonthEnd,
    setSelectedMonthEnd,
    selectedCurrency,
    setSelectedCurrency,
    regionMetricsHistoryLoading,
    regionMetricsHistoryError,
    regionMetricsHistory,
    fetchRegionMetricsHistoryData,
    metricsHistoryUpsertAndSave,
  } = useTreasuryKRISummaryContext();

  const {
    fetchApprovalFlowDataForKey,
    approvalFlowByKey,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
  } = useApprovalContext();

  const fetchedApprovalKeysRef = useRef(new Set());
  const { firstName, lastName, email } = selectedUserDetail || {};

  const handleOpenChange = (open) => {
    if (!open) setIsTouched(true);
    setIsOpen(open);
  };

  const handleSubmit = () => {
    if (selectedMonthEnd) {
      fetchedApprovalKeysRef.current.clear();
      fetchRegionMetricsHistoryData({
        isGlobal: false,
        isKRISummary: true,
        currency: selectedCurrency,
      });
    }
  };

  const currencies = useMemo(() => {
    return ([LOCAL_CURRENCY] || []).map((currency) => mapCurrency(currency));
  }, []);

  const isPercentageValue = (value) =>
    value !== null && value !== undefined && String(value).includes("%");

  const isNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const cleaned = String(value).replace("%", "").trim();
    return cleaned !== "" && !Number.isNaN(Number(cleaned));
  };

  const getMetricOptions = (metric) => {
    if (!metric?.options) return [];
    return metric.options.split("|").map((opt) => opt.trim());
  };

  const hasDropdownOptions = (metric) => getMetricOptions(metric).length > 0;

  const getEditType = (cell) => {
    if (!cell) return "text";
    if (hasDropdownOptions(cell.metric)) return "select";
    if (cell.isPercentage || isNumericValue(cell.currentValue)) return "number";
    return "text";
  };

  const handleOpenEdit = ({
    metric,
    entity,
    reportDate,
    field,
    currentValue,
  }) => {
    const isPercentage = isPercentageValue(currentValue);
    setEditingCell({
      metric,
      entity,
      reportDate,
      field,
      currentValue,
      isPercentage,
    });
    setEditValue(
      isPercentage
        ? String(currentValue ?? "").replace("%", "")
        : String(currentValue ?? ""),
    );
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingCell) return;

    let finalValue = editValue;
    if (editingCell.isPercentage && editValue !== "") {
      finalValue = `${editValue}%`;
    }

    const payload = {
      dimension: editingCell.metric.dimension,
      dimension2: editingCell.metric.dimension2,
      measure: editingCell.metric.measure,
      businessEntityID: editingCell.entity?.entity,
      value: finalValue,
      isGlobal: true,
      monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
      field: editingCell.field,
      limit: editingCell.entity?.limit,
    };

    try {
      await metricsHistoryUpsertAndSave([payload]);
      fetchRegionMetricsHistoryData({
        isGlobal: true,
        isKRISummary: true,
        currency: selectedCurrency,
      });
      setEditOpen(false);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const filteredRegionData = useMemo(() => {
    return (regionMetricsHistory || []).filter(
      (region) =>
        region?.regionName && region.regionName.toLowerCase() !== "global",
    );
  }, [regionMetricsHistory]);

  const regionRowSpans = useMemo(() => {
    const spans = new Map();
    filteredRegionData.forEach((region) => {
      spans.set(region.regionId, (region.sfcs || []).length || 1);
    });
    return spans;
  }, [filteredRegionData]);

  const columnGroups = useMemo(() => {
    const map = new Map();
    filteredRegionData.forEach((region) => {
      (region.sfcs || []).forEach((sfc) => {
        (sfc.metrics || []).forEach((metric) => {
          const key = groupKeyForMetric(metric);
          if (!key) return;
          if (!map.has(key)) {
            map.set(key, {
              groupKey: key,
              label: labelForGroup(key),
              measuresMap: new Map(),
            });
          }
          const group = map.get(key);
          if (!group.measuresMap.has(metric.metricId)) {
            group.measuresMap.set(metric.metricId, metric);
          }
        });
      });
    });

    return Array.from(map.values()).map((group) => ({
      groupKey: group.groupKey,
      label: group.label,
      measures: Array.from(group.measuresMap.values()),
    }));
  }, [filteredRegionData]);

  const allMetricColumns = useMemo(() => {
    return columnGroups.flatMap((group) =>
      group.measures.map((metric) => ({
        ...metric,
        groupKey: group.groupKey,
        groupLabel: group.label,
      })),
    );
  }, [columnGroups]);

  const stickyCellBase = {
    position: "sticky",
    backgroundColor: "#FAFAFA",
    padding: "0px 8px",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle",
  };

  const TFSC_WIDTH = 90;
  const REGION_WIDTH = 150;
  const SFC_WIDTH = 150;
  const METRIC_WIDTH = 260;

  const TOP_HEADER_HEIGHT = 28;
  const SUB_HEADER_HEIGHT = 28;

  const getMetricCellValue = (sfc, metricCol) => {
    const matchedMetric = (sfc.metrics || []).find(
      (m) => m.metricId === metricCol.metricId,
    );
    const entity = matchedMetric?.entities?.[0];
    const currentHistory = entity?.history?.find(
      (h) => h.reportDate === selectedMonthEnd?.format("YYYY-MM-DD"),
    );

    return {
      matchedMetric,
      entity,
      currentHistory,
      currentValue: currentHistory?.value ?? "N/A",
    };
  };

  const buildApprovalKey = (params) =>
    [
      params.region ?? "null",
      params.businessEntityID ?? "null",
      params.measure ?? "null",
      params.dimension ?? "null",
      params.dimension2 ?? "null",
      params.isGlobal ?? "null",
      params.isModelerReport ?? "null",
      params.isTreasuryKRIReport ?? "null",
      params.isKRISummaryReport ?? "null",
      params.isSFCKRIGlobalSummaryReport ?? "null",
      params.isRegionKRIGlobalSummaryReport ?? "null",
      params.isTFSCKRIGlobalSummaryReport ?? "null",
    ].join(APPROVAL_KEY_SEPARATOR);

  const getApprovalBaseParams = (regionName) => ({
    region: regionName,
    businessEntityID: null,
    selectedReportDate: selectedMonthEnd,
    measure: null,
    dimension: null,
    dimension2: null,
    isGlobal: null,
    isModelerReport: null,
    isTreasuryKRIReport: null,
    isKRISummaryReport: null,
    isSFCKRIGlobalSummaryReport: null,
    isRegionKRIGlobalSummaryReport: null,
    isTFSCKRIGlobalSummaryReport: null,
  });

  const buildRegionApprovalParams = (regionName) => ({
    ...getApprovalBaseParams(regionName),
    isRegionKRIGlobalSummaryReport: true,
  });

  const buildTFSCApprovalParams = (regionName) => ({
    ...getApprovalBaseParams(regionName),
    isTFSCKRIGlobalSummaryReport: true,
  });

  const getRegionApprovalKey = (regionName) =>
    buildApprovalKey(buildRegionApprovalParams(regionName));

  const getTFSCApprovalKey = (regionName) =>
    buildApprovalKey(buildTFSCApprovalParams(regionName));

  const getApprovalFlowForRegion = (regionName) =>
    approvalFlowByKey[getRegionApprovalKey(regionName)] || [];

  const getApprovalFlowForTFSC = (regionName) =>
    approvalFlowByKey[getTFSCApprovalKey(regionName)] || [];

  const getLatestApprovalData = (step) => {
    const approvers = step?.approvalStepApprovers || [];
    const allApprovalData = approvers.flatMap(
      (approver) => approver.approvalDataList || [],
    );

    if (allApprovalData.length === 0) return null;

    return allApprovalData.reduce((latest, current) => {
      if (!latest) return current;
      if (!current.approvalStatusChangeTime) return latest;
      if (!latest.approvalStatusChangeTime) return current;

      return new Date(current.approvalStatusChangeTime) >
        new Date(latest.approvalStatusChangeTime)
        ? current
        : latest;
    }, null);
  };

  const getLatestStepEvent = (regionFlow, tfscFlow) => {
    const regionLatest = getLatestApprovalData(regionFlow?.[0]);
    const tfscLatest = getLatestApprovalData(tfscFlow?.[0]);

    const regionTime = regionLatest?.approvalStatusChangeTime
      ? new Date(regionLatest.approvalStatusChangeTime)
      : null;

    const tfscTime = tfscLatest?.approvalStatusChangeTime
      ? new Date(tfscLatest.approvalStatusChangeTime)
      : null;

    if (!regionTime && !tfscTime) return null;

    if (!tfscTime) {
      return {
        type: "REGION",
        status: String(regionLatest?.approvalStatus || "").toUpperCase(),
        time: regionTime,
      };
    }

    if (!regionTime) {
      return {
        type: "TFSC",
        status: String(tfscLatest?.approvalStatus || "").toUpperCase(),
        time: tfscTime,
      };
    }

    if (regionTime >= tfscTime) {
      return {
        type: "REGION",
        status: String(regionLatest?.approvalStatus || "").toUpperCase(),
        time: regionTime,
      };
    }

    return {
      type: "TFSC",
      status: String(tfscLatest?.approvalStatus || "").toUpperCase(),
      time: tfscTime,
    };
  };

  const getRowApprovalUiState = (regionFlow, tfscFlow) => {
    const hasRegionFlow = (regionFlow || []).length > 0;
    const hasTfscFlow = (tfscFlow || []).length > 0;

    const regionLatest = getLatestApprovalData(regionFlow?.[0]);
    const tfscLatest = getLatestApprovalData(tfscFlow?.[0]);

    const latestEvent = getLatestStepEvent(regionFlow, tfscFlow);

    const regionStatus = String(
      regionLatest?.approvalStatus || "",
    ).toUpperCase();
    const tfscStatus = String(tfscLatest?.approvalStatus || "").toUpperCase();

    // If only TFSC flow exists, enable TFSC checkbox
    if (!hasRegionFlow && hasTfscFlow) {
      return {
        regionChecked: false,
        regionDisabled: true,
        tfscChecked: tfscStatus === "APPROVED",
        tfscDisabled: tfscStatus === "APPROVED",
        regionRejected: false,
        tfscRejected: tfscStatus === "REJECTED",
      };
    }

    // If only Region flow exists, enable Region checkbox
    if (hasRegionFlow && !hasTfscFlow) {
      return {
        regionChecked: regionStatus === "APPROVED",
        regionDisabled: regionStatus === "APPROVED",
        tfscChecked: false,
        tfscDisabled: true,
        regionRejected: regionStatus === "REJECTED",
        tfscRejected: false,
      };
    }

    // No flows at all
    if (!hasRegionFlow && !hasTfscFlow) {
      return {
        regionChecked: false,
        regionDisabled: false,
        tfscChecked: false,
        tfscDisabled: true,
        regionRejected: false,
        tfscRejected: false,
      };
    }

    if (!latestEvent) {
      return {
        regionChecked: false,
        regionDisabled: false,
        tfscChecked: false,
        tfscDisabled: true,
        regionRejected: false,
        tfscRejected: false,
      };
    }

    if (latestEvent.type === "REGION") {
      if (latestEvent.status === "APPROVED") {
        return {
          regionChecked: true,
          regionDisabled: true,
          tfscChecked: false,
          tfscDisabled: false,
          regionRejected: false,
          tfscRejected: tfscStatus === "REJECTED",
        };
      }

      if (latestEvent.status === "REJECTED") {
        return {
          regionChecked: false,
          regionDisabled: false,
          tfscChecked: false,
          tfscDisabled: true,
          regionRejected: true,
          tfscRejected: false,
        };
      }
    }

    if (latestEvent.type === "TFSC") {
      if (latestEvent.status === "REJECTED") {
        return {
          regionChecked: false, // IMPORTANT: reopen Region for re-review
          regionDisabled: false, // IMPORTANT: enable Region
          tfscChecked: false,
          tfscDisabled: true,
          regionRejected: false,
          tfscRejected: true,
        };
      }

      if (latestEvent.status === "APPROVED") {
        return {
          regionChecked: true,
          regionDisabled: true,
          tfscChecked: true,
          tfscDisabled: true,
          regionRejected: false,
          tfscRejected: false,
        };
      }
    }

    return {
      regionChecked: false,
      regionDisabled: false,
      tfscChecked: false,
      tfscDisabled: true,
      regionRejected: false,
      tfscRejected: false,
    };
  };

  const openApprovalPopup = (regionName, type) => {
    setPendingApprovalRegion(regionName);
    setPendingApprovalType(type);
    setApprovalComment("");
    setApprovalError("");
    setApprovalOpen(true);
  };

  const handleRegionCheckboxChange = (regionName, checked) => {
    if (!checked) return;
    openApprovalPopup(regionName, "REGION");
  };

  const handleTFSCCheckboxChange = (regionName, checked) => {
    if (!checked) return;
    openApprovalPopup(regionName, "TFSC");
  };

  const refreshApprovalFlowForSelectedRegion = async (regionName) => {
    await fetchApprovalFlowDataForKey({
      region: regionName,
      businessEntityID: null,
      selectedReportDate: selectedMonthEnd,
      measure: null,
      dimension: null,
      dimension2: null,
      isGlobal: null,
      isModelerReport: null,
      isTreasuryKRIReport: null,
      isKRISummaryReport: null,
      isSFCKRIGlobalSummaryReport: null,
      isRegionKRIGlobalSummaryReport: null,
      isTFSCKRIGlobalSummaryReport: null,
    });
  };

  const handleApprovalAction = async (type) => {
    if (!pendingApprovalRegion || !pendingApprovalType) return;

    const rowFlow =
      pendingApprovalType === "REGION"
        ? getApprovalFlowForRegion(pendingApprovalRegion)
        : getApprovalFlowForTFSC(pendingApprovalRegion);

    const currentStep =
      rowFlow.find((step) => {
        const latest = getLatestApprovalData(step);
        return (
          String(latest?.approvalStatus || "PENDING").toUpperCase() ===
          "PENDING"
        );
      }) || rowFlow[0];

    const currentApprover = currentStep?.approvalStepApprovers?.find(
      (a) => a.email?.toLowerCase() === email?.toLowerCase(),
    );

    if (!currentApprover) {
      alert("You are not authorized to do this action");
      return;
    }

    if (!approvalComment.trim()) {
      setApprovalError("Comment is mandatory");
      return;
    }

    if (approvalComment.length > 200) {
      setApprovalError("Comment cannot be more than 200 characters");
      return;
    }

    try {
      await saveApprovalData({
        currentApprover,
        type,
        comment: approvalComment,
        reportDate: selectedMonthEnd,
      });

      await refreshApprovalFlowForSelectedRegion(pendingApprovalRegion);
      setApprovalOpen(false);

      await sendApproverEmailNotifyForSelectedReport({
        region: pendingApprovalRegion,
        businessEntityID: null,
        selectedReportDate: selectedMonthEnd,
        measure: null,
        dimension: null,
        dimension2: null,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });

      setPendingApprovalRegion(null);
      setPendingApprovalType(null);
      setApprovalComment("");
      setApprovalError("");
    } catch (error) {
      console.error(
        "Error saving approval data or fetching updated flow:",
        error,
      );
    }
  };

  useEffect(() => {
    if (!selectedMonthEnd) return;
    if (!filteredRegionData?.length) return;

    const reportDate = selectedMonthEnd.format("YYYY-MM-DD");
    filteredRegionData.forEach((region) => {
      if (!region?.regionName) return;

      const requestKey = `${region.regionName}|${reportDate}`;
      if (fetchedApprovalKeysRef.current.has(requestKey)) return;

      fetchedApprovalKeysRef.current.add(requestKey);

      fetchApprovalFlowDataForKey({
        region: region.regionName,
        businessEntityID: null,
        selectedReportDate: selectedMonthEnd,
        measure: null,
        dimension: null,
        dimension2: null,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    });
  }, [filteredRegionData, selectedMonthEnd, fetchApprovalFlowDataForKey]);

  const renderApprovalHistoryTooltip = (flow) => {
    const step = flow?.[0];

    if (!step) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: "#ff3e3e" }}
          >
            No Approval Data found!
          </Typography>
        </Box>
      );
    }

    const allApprovalData = (step.approvalStepApprovers || []).flatMap(
      (approver) =>
        (approver.approvalDataList || []).map((ad) => ({
          ...ad,
          approverEmail: approver.email,
          approverIsActive: approver.approverIsActive,
        })),
    );

    const sorted = [...allApprovalData].sort(
      (a, b) =>
        new Date(b.approvalStatusChangeTime) -
        new Date(a.approvalStatusChangeTime),
    );

    return (
      <Box sx={{ p: 1, maxWidth: 360 }}>
        <Typography
          sx={{
            fontWeight: 700,
            mb: 0.5,
            textDecoration: "underline",
            fontSize: 13,
            lineHeight: 1.3,
          }}
        >
          Step Description
        </Typography>

        <Typography
          sx={{
            mb: 1,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1.35,
            whiteSpace: "pre-wrap",
          }}
        >
          {step.approvalDescription}
        </Typography>

        <Typography
          sx={{
            fontWeight: 700,
            mb: 0.5,
            textDecoration: "underline",
            fontSize: 13,
            lineHeight: 1.3,
          }}
        >
          Assigned Reviewers
        </Typography>
        <Box sx={{ mb: 1 }}>
          {(step.approvalStepApprovers || []).map((approver) => (
            <Typography
              key={approver.email}
              variant="caption"
              sx={{
                display: "block",
                fontSize: 12,
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontStyle: approver.approverIsActive ? "normal" : "italic",
                color: approver.approverIsActive ? "#fcfcfc" : "#999",
              }}
              title={approver.email}
            >
              {approver.email}
              {!approver.approverIsActive && " (Inactive)"}
            </Typography>
          ))}
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            mb: 0.75,
            textDecoration: "underline",
            fontSize: 13,
            lineHeight: 1.3,
          }}
        >
          Reviewers History:
        </Typography>
        {!allApprovalData.length ? (
          <span style={{ fontWeight: "600", color: "#ff3e3e" }}>
            No Approval Data found!
          </span>
        ) : (
          <>
            {sorted.map((ad, idx) => {
              const status = String(ad.approvalStatus || "").toUpperCase();
              const statusColor =
                status === "APPROVED"
                  ? "#2e7d32"
                  : status === "REJECTED"
                    ? "#d32f2f"
                    : "#1976d2";

              return (
                <Box
                  key={`${ad.approvalStepApproverStepApprovalId}-${idx}`}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.4,
                      fontSize: 11.5,
                      color: "#555",
                    }}
                  >
                    <strong>Approver:</strong>{" "}
                    <span style={{ color: "#000", fontStyle: "italic" }}>
                      {ad.approverEmail}
                      {!ad.approverIsActive && " (Inactive)"}
                    </span>
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 0.4,
                      fontSize: 11.5,
                      color: "#555",
                    }}
                  >
                    <strong>Status:</strong>{" "}
                    <span
                      style={{
                        color: statusColor,
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {status}
                    </span>{" "}
                    <span style={{ color: "#333" }}>
                      ({getIsoStringToDate(ad.approvalStatusChangeTime)})
                    </span>
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      whiteSpace: "pre-wrap",
                      fontSize: 11.5,
                      color: "#555",
                    }}
                  >
                    <strong>Comments:</strong>{" "}
                    <span style={{ color: "#000", fontStyle: "italic" }}>
                      {ad.approvalComments || "N/A"}
                    </span>
                  </Typography>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    );
  };

  return (
    <Card style={{ padding: "6px", marginBottom: "20px", width: "100%" }}>
      <Typography
        sx={{ color: "#6a6a6a", marginBottom: 2, fontWeight: "bold" }}
      >
        The below report shows the KRI Summary Submission (Global) for the
        selected filters.
      </Typography>

      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date"}>Month End & Year</InputLabel>
          {selectedUserDetailLoading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={185} height={40} />
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={0}>
                <DatePickerMonthEnds
                  minDate={minDate}
                  maxDate={maxDate}
                  value={selectedMonthEnd}
                  onChange={(newValue) => {
                    fetchedApprovalKeysRef.current.clear();
                    setSelectedMonthEnd(newValue?.endOf("month"));
                  }}
                  onOpenChange={handleOpenChange}
                  isOpen={isOpen}
                />
              </Box>
              <Box>
                {selectedMonthEnd === null && isTouched && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      fontStyle: "italic",
                    }}
                  >
                    Month End & Year is required
                  </span>
                )}
              </Box>
            </>
          )}
        </Grid>

        {/* currency dropdown not used anywhere so far */}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Currency"
            labelId="currency"
            options={currencies}
            selectedValue={selectedCurrency}
            handleChange={setSelectedCurrency}
            placeholder={"Select Currency"}
            dropdownWidth={150}
            isLoading={selectedUserDetailLoading}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {selectedUserDetailLoading ? (
            <Box display="flex" alignItems="center" sx={{ mt: 3 }}>
              <Skeleton variant="rectangular" width={130} height={40} />
            </Box>
          ) : (
            <TextField
              id="user"
              label="User"
              value={`${firstName || ""} ${lastName || ""}`.trim()}
              disabled
              fullWidth
              size="small"
              sx={{ mt: 3 }}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSubmit}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              !selectedMonthEnd ||
              !selectedCurrency
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {regionMetricsHistoryLoading ? (
            <>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                animation="pulse"
                style={{ marginBottom: 8 }}
              />
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={14}
                  animation="pulse"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </>
          ) : !regionMetricsHistoryLoading && regionMetricsHistoryError ? (
            <Item>
              <Typography
                variant="body1"
                sx={{ color: "#adadad", minHeight: "250px" }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!regionMetricsHistoryLoading && !regionMetricsHistory) ||
            (!regionMetricsHistoryLoading && !filteredRegionData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{ color: "#adadad", minHeight: "250px" }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: 600,
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  borderRadius: "3px",
                  boxShadow: "1px 1px 2px",
                  minWidth: "max-content",
                  tableLayout: "fixed",
                }}
              >
                <TableHead sx={{ backgroundColor: "#eeeeee" }}>
                  <TableRow sx={{ height: TOP_HEADER_HEIGHT }}>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...stickyCellBase,
                        top: 0,
                        left: 0,
                        zIndex: 6,
                        fontWeight: "bold",
                        width: TFSC_WIDTH,
                        minWidth: TFSC_WIDTH,
                        maxWidth: TFSC_WIDTH,
                        textAlign: "center",
                        borderRight: "1px solid #d9d9d9",
                        backgroundColor: "#eeeeee",
                        height: TOP_HEADER_HEIGHT + SUB_HEADER_HEIGHT,
                      }}
                    >
                      TFSC
                    </TableCell>

                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...stickyCellBase,
                        top: 0,
                        left: TFSC_WIDTH,
                        zIndex: 6,
                        fontWeight: "bold",
                        width: REGION_WIDTH,
                        minWidth: REGION_WIDTH,
                        maxWidth: REGION_WIDTH,
                        textAlign: "center",
                        borderRight: "1px solid #d9d9d9",
                        backgroundColor: "#eeeeee",
                        height: TOP_HEADER_HEIGHT + SUB_HEADER_HEIGHT,
                      }}
                    >
                      REGION
                    </TableCell>

                    <TableCell
                      rowSpan={2}
                      sx={{
                        ...stickyCellBase,
                        top: 0,
                        left: TFSC_WIDTH + REGION_WIDTH,
                        zIndex: 6,
                        fontWeight: "bold",
                        width: SFC_WIDTH,
                        minWidth: SFC_WIDTH,
                        maxWidth: SFC_WIDTH,
                        textAlign: "center",
                        borderRight: "1px solid #d9d9d9",
                        backgroundColor: "#eeeeee",
                        height: TOP_HEADER_HEIGHT + SUB_HEADER_HEIGHT,
                      }}
                    >
                      SFC
                    </TableCell>

                    {columnGroups.map((group) => (
                      <TableCell
                        key={group.groupKey}
                        colSpan={group.measures.length}
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 5,
                          fontWeight: "bold",
                          backgroundColor: "#eeeeee",
                          minWidth: METRIC_WIDTH * group.measures.length,
                          width: METRIC_WIDTH * group.measures.length,
                          textAlign: "center",
                          borderRight: "1px solid #d9d9d9",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          lineHeight: 1,
                          padding: "0px 8px",
                          height: TOP_HEADER_HEIGHT,
                          verticalAlign: "middle",
                        }}
                      >
                        {group.label}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableRow sx={{ height: SUB_HEADER_HEIGHT }}>
                    {allMetricColumns.map((metric, index) => {
                      const isLastMetricInGroup =
                        index === allMetricColumns.length - 1 ||
                        metric.groupKey !==
                          allMetricColumns[index + 1]?.groupKey;

                      return (
                        <TableCell
                          key={metric.metricId}
                          sx={{
                            position: "sticky",
                            top: TOP_HEADER_HEIGHT,
                            zIndex: 5,
                            fontWeight: "bold",
                            backgroundColor: "#eeeeee",
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            minWidth: METRIC_WIDTH,
                            width: METRIC_WIDTH,
                            maxWidth: METRIC_WIDTH,
                            textAlign: "center",
                            borderRight: isLastMetricInGroup
                              ? "1px solid #d9d9d9"
                              : "1px solid #eeeeee",
                            lineHeight: 1,
                            padding: "0px 8px",
                            height: SUB_HEADER_HEIGHT,
                            verticalAlign: "middle",
                          }}
                        >
                          {metric.measure}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRegionData.map((region) =>
                    (region.sfcs || []).map((sfc, sfcIndex) => {
                      const regionFlow = getApprovalFlowForRegion(
                        region.regionName,
                      );
                      const tfscFlow = getApprovalFlowForTFSC(
                        region.regionName,
                      );

                      const regionHasData = regionFlow.length > 0;
                      const tfscHasData = tfscFlow.length > 0;

                      const {
                        regionChecked,
                        regionDisabled,
                        tfscChecked,
                        tfscDisabled,
                        regionRejected,
                        tfscRejected,
                      } = getRowApprovalUiState(regionFlow, tfscFlow);

                      return (
                        <TableRow key={`${region.regionId}-${sfc.sfcId}`}>
                          {sfcIndex === 0 && (
                            <TableCell
                              rowSpan={regionRowSpans.get(region.regionId)}
                              sx={{
                                ...stickyCellBase,
                                left: 0,
                                zIndex: 3,
                                width: TFSC_WIDTH,
                                minWidth: TFSC_WIDTH,
                                maxWidth: TFSC_WIDTH,
                                textAlign: "center",
                                verticalAlign: "middle",
                                borderRight: "1px solid #d9d9d9",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <span>TFSC</span>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {tfscHasData && (
                                    <Checkbox
                                      size="small"
                                      checked={tfscChecked}
                                      disabled={tfscDisabled}
                                      onChange={(e) =>
                                        handleTFSCCheckboxChange(
                                          region.regionName,
                                          e.target.checked,
                                        )
                                      }
                                      sx={{
                                        p: 0.25,
                                        color: tfscRejected
                                          ? "#d32f2f"
                                          : tfscDisabled
                                            ? "#2e7d32"
                                            : "#1976d2",
                                        "&.Mui-checked": {
                                          color: tfscRejected
                                            ? "#d32f2f"
                                            : tfscDisabled
                                              ? "#2e7d32"
                                              : "#1976d2",
                                        },
                                      }}
                                    />
                                  )}
                                  <Tooltip
                                    title={
                                      tfscHasData
                                        ? renderApprovalHistoryTooltip(tfscFlow)
                                        : "No approval steps found!"
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon
                                      sx={{
                                        fontSize: 18,
                                        cursor: "pointer",
                                        color: "#4992f7",
                                      }}
                                    />
                                  </Tooltip>
                                </Box>

                                <Box
                                  sx={{
                                    mt: 0.5,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    textAlign: "center",
                                    width: "100%",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {tfscFlow?.[0]?.approvalStepName}
                                </Box>
                              </Box>
                            </TableCell>
                          )}

                          {sfcIndex === 0 && (
                            <TableCell
                              rowSpan={regionRowSpans.get(region.regionId)}
                              sx={{
                                ...stickyCellBase,
                                left: TFSC_WIDTH,
                                zIndex: 3,
                                width: REGION_WIDTH,
                                minWidth: REGION_WIDTH,
                                maxWidth: REGION_WIDTH,
                                textAlign: "center",
                                verticalAlign: "middle",
                                borderRight: "1px solid #d9d9d9",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <span>{region.regionName}</span>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {regionHasData && (
                                    <Checkbox
                                      size="small"
                                      checked={regionChecked}
                                      disabled={regionDisabled}
                                      onChange={(e) =>
                                        handleRegionCheckboxChange(
                                          region.regionName,
                                          e.target.checked,
                                        )
                                      }
                                      sx={{
                                        p: 0.25,
                                        color: regionRejected
                                          ? "#d32f2f"
                                          : regionDisabled
                                            ? "#2e7d32"
                                            : "#1976d2",
                                        "&.Mui-checked": {
                                          color: regionRejected
                                            ? "#d32f2f"
                                            : regionDisabled
                                              ? "#2e7d32"
                                              : "#1976d2",
                                        },
                                      }}
                                    />
                                  )}
                                  <Tooltip
                                    title={
                                      regionHasData
                                        ? renderApprovalHistoryTooltip(
                                            regionFlow,
                                          )
                                        : "No approval steps found!"
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <InfoOutlinedIcon
                                      sx={{
                                        fontSize: 18,
                                        cursor: "pointer",
                                        color: "#4992f7",
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                    textAlign: "center",
                                    width: "100%",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {regionFlow?.[0]?.approvalStepName}
                                </Box>
                              </Box>
                            </TableCell>
                          )}

                          <TableCell
                            sx={{
                              ...stickyCellBase,
                              left: TFSC_WIDTH + REGION_WIDTH,
                              zIndex: 3,
                              width: SFC_WIDTH,
                              minWidth: SFC_WIDTH,
                              maxWidth: SFC_WIDTH,
                              textAlign: "center",
                              verticalAlign: "middle",
                              borderRight: "1px solid #d9d9d9",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              <span>{sfc.businessEntityID}</span>
                              <ApprovalDot status={sfc?.approvalStatus} />
                            </Box>
                          </TableCell>

                          {allMetricColumns.map((metricCol) => {
                            const {
                              matchedMetric,
                              entity,
                              currentHistory,
                              currentValue,
                            } = getMetricCellValue(
                              { ...sfc, metrics: sfc.metrics || [] },
                              metricCol,
                            );

                            const showPercentage =
                              isPercentageValue(currentValue);
                            const displayValue = showPercentage
                              ? String(currentValue).replace("%", "")
                              : currentValue;

                            const currentMetricIndex =
                              allMetricColumns.findIndex(
                                (m) => m.metricId === metricCol.metricId,
                              );
                            const isLastMetricInGroup =
                              currentMetricIndex ===
                                allMetricColumns.length - 1 ||
                              metricCol.groupKey !==
                                allMetricColumns[currentMetricIndex + 1]
                                  ?.groupKey;

                            return (
                              <TableCell
                                key={`${sfc.sfcId}-${metricCol.metricId}`}
                                sx={{
                                  minWidth: METRIC_WIDTH,
                                  width: METRIC_WIDTH,
                                  maxWidth: METRIC_WIDTH,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  borderRight: isLastMetricInGroup
                                    ? "1px solid #d9d9d9"
                                    : "1px solid #eeeeee",
                                  padding: "6px 10px",
                                }}
                              >
                                {matchedMetric ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <span>
                                      {displayValue}
                                      {showPercentage ? "%" : ""}
                                    </span>
                                    {false && (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleOpenEdit({
                                            metric: matchedMetric,
                                            entity,
                                            reportDate:
                                              selectedMonthEnd?.format(
                                                "YYYY-MM-DD",
                                              ),
                                            field: "history",
                                            currentValue,
                                          })
                                        }
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    <ApprovalDot
                                      status={currentHistory?.approvalStatus}
                                    />
                                  </Box>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    }),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Approval Action
          <IconButton
            aria-label="close"
            onClick={() => setApprovalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Region:</strong> {pendingApprovalRegion || "N/A"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Type:</strong> {pendingApprovalType || "N/A"}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={approvalComment}
            placeholder="Enter Comments within 200 Characters..."
            onChange={(e) => {
              setApprovalComment(e.target.value);
              setApprovalError("");
            }}
            error={!!approvalError}
            helperText={approvalError}
          />
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleApprovalAction("REJECTED")}
          >
            Reject
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={() => handleApprovalAction("APPROVED")}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Value</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Category:</strong>{" "}
            {editingCell?.metric?.dimension2
              ? `${editingCell.metric.dimension} -> ${editingCell.metric.dimension2}`
              : editingCell?.metric?.dimension}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Measure:</strong> {editingCell?.metric?.measure}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Current Value:</strong> {editingCell?.currentValue || "N/A"}
          </Typography>

          {getEditType(editingCell) === "select" ? (
            <FormControl fullWidth size="small">
              <InputLabel id="edit-value-label">Value</InputLabel>
              <Select
                labelId="edit-value-label"
                value={editValue}
                label="Value"
                onChange={(e) => setEditValue(e.target.value)}
              >
                {getMetricOptions(editingCell?.metric).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Value"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              type={getEditType(editingCell) === "number" ? "number" : "text"}
              inputProps={
                getEditType(editingCell) === "number"
                  ? { inputMode: "decimal" }
                  : {}
              }
              InputProps={
                editingCell?.isPercentage
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default KRISummarySubmissionGlobalFiltersAndReports;
